/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import React, {
  useEffect, useState, useMemo
} from 'react';
import classnames from 'classnames';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { IlButton, IlInlineLoading, IlTextInput } from 'il-framework-component';
import { I18nNamespaces } from '../../../../app/shared/utils/constants.util';
import { RootState } from '../../../../app/store';
import { showToast } from '../../../../app/shared/AppSlice/AppSlice';
import {
  verifyOrActivateFactor, setError, setValidationError, selectFactorForVerify, loadSecuritySetupPage
} from '../../setupSecuritySlice';
import styles from './VerifyAuthenticator.scss';
import { getLoaValidationMessageKey, useRequiredLevel, capitalizeFirstLetter } from '../../../../app/shared/utils/helpers.util';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';
import { SecuritySetupRoutes } from '../../../../app/shared/utils/securitySetupRoutes';

export default function VerifyAuthenticator() {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const requiredLevel = useRequiredLevel();
  const { replaceLoaLevel, goBackOrReplace } = usePreserveSearchNav();
  const { search } = useLocation();
  const factorIdFromUrl = new URLSearchParams(search || '').get('factorId') ?? '';
  const pageLoading = useSelector((state: RootState) => state.setupSecurity.pageLoading);
  const actionLoading = useSelector((state: RootState) => state.setupSecurity.actionLoading);
  const enrolled = useSelector((state: RootState) => state.setupSecurity.enrolledFactors);
  const verifyFactorData = useSelector((state: RootState) => state.setupSecurity.selectedFactor);
  const validationError = useSelector((state: RootState) => state.setupSecurity.validationError);
  const [code, setCode] = useState('');
  const typedDigits = code.replace(/\D/g, '').length;
  const digits = verifyFactorData?.digits ?? 6;
  const factorId = verifyFactorData?.factorId;

  const factorLabel = useMemo(() => {
    if (!factorId) return '';
    const factor = enrolled.find((f) => f.id === factorId);
    return factor?.label ? capitalizeFirstLetter(factor.label) : '';
  }, [factorId, enrolled]);

  const canSubmit = !!factorId
    && !actionLoading
    && !validationError
    && typedDigits === digits;

  useEffect(() => {
    if (!enrolled.length) dispatch(loadSecuritySetupPage(t));
    return () => {
      dispatch(setError(''));
      dispatch(setValidationError(''));
    };
  }, [dispatch, requiredLevel, t, enrolled.length]);

  useEffect(() => {
    if (pageLoading || verifyFactorData) return;
    if (!factorIdFromUrl) {
      goBackOrReplace(SecuritySetupRoutes.root);
      return;
    }
    if (!enrolled.length) return;
    dispatch(selectFactorForVerify(factorIdFromUrl));
  }, [dispatch, pageLoading, enrolled, verifyFactorData, factorIdFromUrl, goBackOrReplace]);

  useEffect(() => {
    if (!verifyFactorData) return;
    if (typeof verifyFactorData.loaLevel === 'number' && verifyFactorData.loaLevel !== requiredLevel) {
      dispatch(showToast({
        title: t('loaEnrichment.levelAdjustedVerify', { level: verifyFactorData.loaLevel }),
        timeout: 0,
        kind: 'info',
        subtitle: ''
      }));
      replaceLoaLevel(verifyFactorData.loaLevel);
    }
  }, [dispatch, verifyFactorData, requiredLevel, t, replaceLoaLevel]);

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setError(''));
    dispatch(setValidationError(''));

    const input = e.currentTarget.value.replace(/\D/g, '');
    const clippedCode = input.slice(0, digits);
    setCode(clippedCode);
  };

  const submit = (ev: React.FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    const otp = code.replace(/\s/g, '');
    if (verifyFactorData?.factorId) {
      dispatch(verifyOrActivateFactor(t, {
        factorId: verifyFactorData?.factorId,
        code: otp,
        action: 'verify',
        successToastMsg: 'loaEnrichment.common.messages.verified',
        elevateLevel: verifyFactorData?.loaLevel ?? requiredLevel
      }));
    }
  };

  return (
    <>
      <h1 className={classnames(styles.formTitle, styles.title)}>{t('loaEnrichment.common.titles.verifyIdentity')}</h1>
      {factorLabel && (
        <p className={styles.helpText}>
          <strong>{factorLabel}</strong>
        </p>
      )}
      <p className={styles.helpText}>{t('loaEnrichment.factors.authenticator.verifyHelpText', {
        digits
      })}
      </p>
      <ul className={styles.helpTextOptions}>
        <li className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.microsoft')}</li>
        <li className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.google')}</li>
        <li className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.oneLogin')}</li>
        <li className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.authy')}</li>
        <li className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.lastPass')}</li>
      </ul>
      <form onSubmit={submit}>
        <div className={styles.verificationCodeInput}>
          <IlTextInput
            id='verification-code'
            labelText={t('loaEnrichment.common.labels.verificationCode')}
            placeholder={t('loaEnrichment.common.placeholders.code', {
              digits
            })}
            type='text'
            invalid={!!validationError}
            invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
            maxLength={digits}
            value={code}
            onChange={updateCode}
            autoFocus
            autoComplete='off'
          />
        </div>
        <div className={styles.activationContainer}>
          <IlButton
            id='testActiveButton'
            kind='primary'
            type='submit'
            disabled={!canSubmit}
          > {t('loaEnrichment.common.buttons.verify')}
          </IlButton>
        </div>
      </form>
      {actionLoading && (
        <div className={styles.loadingContainer}>
          <IlInlineLoading description={t('loaEnrichment.common.status.pleaseWait')} success={false} onSuccess={noop} />
        </div>
      )}
    </>
  );
}
